import { useState, useEffect, useCallback } from 'react';
import { Table, Header, Container, Button, Input } from '../../../../../core/components';
import { ITableColumnType } from '../../../../../core/interfaces';
import { StepsClients as ModelClient } from '../model/steps-clients.model';
import { SaveOutlined, PlusOutlined } from '@ant-design/icons';
import { StepsClientsService } from '../services/steps-clients.service';
import { ProfileService } from '../../profile/service/profile.service';
import { UtilService } from '../../../../../core/services/util.service';
import { Spin } from 'antd';

const columns: ITableColumnType<ModelClient>[] = [
  { title: 'Name', dataIndex: 'name', key: 'name', filter: true },
  { title: 'Website', dataIndex: 'website', key: 'website', filter: true },
];

const StepsClients = () => {
  const [data, setData] = useState<ModelClient[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [newClient, setNewClient] = useState<ModelClient>({ name: '', website: '', active: true });
  const [companyId, setCompanyId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const isSaveDisabled = !newClient.name || !newClient.website;

  useEffect(() => {
    const getProfile = async () => {
      try {
        const profile = await ProfileService.Get();
        setCompanyId(profile.companyId);
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    getProfile();
  }, []);

  useEffect(() => {
    const getClients = async (companyId: string) => {
      try {
        const clients = await StepsClientsService.Get(companyId);
        setData(clients); 
      } catch (error) {
        console.error('Error fetching clients:', error);
      } finally {
        setLoading(false); 
      }
    };
    if (companyId) getClients(companyId);
  }, [companyId]);

  const addNew = useCallback(() => setShowForm(true), []);

  const onChangeInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewClient((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const saveClient = async () => {
    if (!newClient.name || !newClient.website) {
      console.error('Both fields are required!');
      return;
    }

    if (!companyId) {
      console.error('Company ID is missing');
      return;
    }

    try {
      const clientData = new ModelClient(newClient.name, newClient.website, newClient.active);

      await StepsClientsService.Post(companyId, clientData);

      const refreshedClients = await StepsClientsService.Get(companyId);
      setData(refreshedClients);

      setShowForm(false);
      setNewClient({ name: '', website: '', active: true });
      UtilService.Alert('Success!', 'success', 'Client added to your list!');
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  return (
    <Container>
      <Header title='Clients'
       buttons={
      !showForm
        ? [
            {
              icon: { value: <PlusOutlined />, size: 'text-lg' },
              text: { value: 'Add New', weight: 400, size: 'text-md' },
              color: 'blue',
              tone: 500,
              onClick: addNew,
            },
          ]
        : []
    } />
      {loading ? (
        <div className='flex justify-center items-center p-4'>
          <Spin size='large' />
        </div>
      ) : (
        <Table<ModelClient> columns={columns} dataSource={data} rowKey='id' />
      )}

      {showForm && (
        <div className='mt-4 p-4 border rounded bg-gray-100 space-y-6'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-5'>
            <Input
              title='Name'
              type='text'
              id='clientName'
              name='name'
              placeholder='Enter client name'
              value={newClient.name}
              OnChange={onChangeInput}
              validation={{ Validator: () => newClient.name === '' }}
              autoFocus
            />
            <Input
              title='Website'
              type='text'
              id='clientWebsite'
              name='website'
              placeholder='Enter client website'
              value={newClient.website}
              OnChange={onChangeInput}
              validation={{ Validator: () => newClient.website === '' }}
            />
          </div>
          <div className='flex justify-end mt-4'>
            <Button
              icon={{ value: <SaveOutlined />, size: 'text-lg' }}
              text={{ value: 'Save', weight: 400, size: 'text-md' }}
              color='blue'
              tone={500}
              disabled={isSaveDisabled}
              onClick={saveClient}
            />
          </div>
        </div>
      )}

      
    </Container>
  );
};

export default StepsClients;